import { } from '@angular/core';
import { Coordinates } from '../shared/geo/ovs.geocoding';
import { ResortSummary, ResortDetail, ResortInventory } from './presentation.model';
import { Entity, Core } from '../shared/core/ovs.core';
import { SafeResourceUrl, DomSanitizer } from "@angular/platform-browser";
import * as moment from 'moment';

export class ResortFeature
{
    private _displayText: string;
    private _value: string;
    private _isSelected: boolean = false;
    private _count: number = 0;

    constructor(displayText: string, value: string, count: number)
    {
        this.DisplayText = displayText;
        this.Value = value;
        this.Count = count;
    }

    public get DisplayText(): string { return this._displayText; }
    public set DisplayText(value: string) { this._displayText = value; }
    public get Value(): string { return this._value; }
    public set Value(value: string) { this._value = value; }
    public get IsSelected(): boolean { return this._isSelected; }
    public set IsSelected(value: boolean) { this._isSelected = value; }
    public get Count(): number { return this._count; }
    public set Count(value: number) { this._count = value; }
}

export class SearchState
{
    private _latLong: Coordinates;
    private _results: ResortSummary[];
    private _startDate: Date;
    private _originalSearchText: string;

    constructor(latLong: Coordinates, results: ResortSummary[], startDate: Date, originalSearchText: string)
    {
        this._latLong = latLong;
        this._results = results;
        this._startDate = startDate;
        this._originalSearchText = originalSearchText;
    }

    public get Results(): ResortSummary[]
    {
        return this._results;
    }

    public get LatLong(): Coordinates
    {
        return this._latLong;
    }

    public get StartDate(): Date
    {
        return this._startDate;
    }

    public get OriginalSearchText(): string
    {
        return this._originalSearchText;
    }
}

export class FilterState
{
    private _priceFrom: number;
    private _priceTo: number;
    private _nights: number[];
    private _occupancy: number;

    constructor(priceFrom: number, priceTo: number, nights: number[], occupancy: number)
    {
        this._priceFrom = priceFrom;
        this._priceTo = priceTo;
        this._nights = nights;
        this._occupancy = occupancy;
    }

    public get PriceFrom(): number { return this._priceFrom; }
    public get PriceTo(): number { return this._priceTo; }
    public get Nights(): number[] { return this._nights; }
    public get Occupancy(): number { return this._occupancy; }
}

export class DisplayValue<T>
{
    private _value: T;
    private _displayText: string;

    constructor(value: T, displayText: string)
    {
        this._value = value;
        this._displayText = displayText;
    }

    public get Value(): T { return this._value; }
    public get DisplayText(): string { return this._displayText; }
}

export class ResortAvailabilityExtension extends Entity
{
    public date: Date;
    public isSelected: boolean;
    public isAvailable: boolean;
}

export class ResortSummaryExtension extends Entity
{
    public nightsDisplayText: string;
    public selectedResortOutlook: any;
}

export class ResortDetailExtension extends Entity
{
    public distanceToAirport: string;
    public nearestAirport: string;
    public resortDescription: string;
    public overview: string;

    public get hasDistanceToAirport(): boolean { return !String.isNullOrWhiteSpace(this.distanceToAirport); }
    public get hasNearestAirport(): boolean { return !String.isNullOrWhiteSpace(this.nearestAirport); }
    public get hasResortDescription(): boolean { return !String.isNullOrWhiteSpace(this.resortDescription); }
    public get hasOverview(): boolean { return !String.isNullOrWhiteSpace(this.overview); }
    
}

export class ResortImageExtension extends Entity
{
    private _safeUrl: SafeResourceUrl;

    public imagePath: number;
    public fileName: string;

    public get safeUrl(): SafeResourceUrl
    {
        if (this._safeUrl)
            return this._safeUrl;

        let sanitizer: DomSanitizer = Core.current.getRequiredService<DomSanitizer>(DomSanitizer);

        this._safeUrl = sanitizer.bypassSecurityTrustResourceUrl(this.fileName);

        return this._safeUrl;
    }
}

export class ResortInventoryExtension extends Entity
{
    public discountRate: number;
    public resortFee: number;
    public serviceFee: number;
    public savings: number;
    public tax: number;
    public price: number;
    public nights: number;
    public unitType: string;
    public unitTypeName: string;
    public creditsUsed: number;
    public insurance: number;
    public cost: number;
    public commissionColor: string;
    public purchaseTypeId: number;
    public reward: number;
    public flatTax: number;
    public get rewardPercent() : number{
        return Math.round(100*this["reward"]/this.price);
    }
    public get hasDiscount(): boolean {
        return this["discountPurchaseTypeId"] > 0 && this.discountedPrice < this.price;
    }
    public get hasReward():boolean{
        return this["discountPurchaseTypeId"] > 0 && this["reward"] > 0;
    }
    public get classes(): any
    {
        let result: any = {};

        if (Core.current.currentConfiguration.IsAgent)
        {
            result['agent-gold-button'] = this.commissionColor === 'Gold';
            result['agent-green-button'] = this.commissionColor === 'Green';
            result['agent-yellow-button'] = this.commissionColor === 'Yellow';
            result['agent-blue-button'] = this.commissionColor === 'Blue';
            result['agent-black-button'] = this.commissionColor === 'Black';
            result['agent-red-button'] = this.commissionColor === 'Red';
            result['agent-purple-button'] = this.commissionColor==='Purple';
        }

        return result;
    }

    public getMemberRate(showPricePerDay: boolean): number{
        return showPricePerDay ? (this.hasDiscount ? this.dailyDiscountedPrice : this.dailyPrice)
        : (this.hasDiscount ? this.discountedPrice : this.price);
    }

    public get margin(): number
    {
        return this.price - this.cost;
    }

    public get taxesAndFees(): number
    {
        let value: number = 0;

        if (this.resortFee)
            value += this.resortFee;

        if (this.serviceFee)
            value += this.serviceFee;

        if (this.tax)
            value += this.tax;

        if (this.flatTax)
            value += this.flatTax;
        return value;
    }

    public get discountedPrice(): number
    {
        return this.discountRate;
    }

    public get dailyDiscountedPrice(): number
    {
        return this.discountRate / this.nights;
    }

    public get dailySavings(): number
    {
        return this.dailyPrice - this.dailyDiscountedPrice;
    }

    public get dailyRewards(): number{
        return this.reward/this.nights;
    }

    public get percentSavings(): number
    {
        if (!this.savings)
            return 0;

        return Math.floor((this.savings / this.price) * 100);
    }

	public get actualPercentSavings(): number {
		let totalPrice: number;
		let discount: number;
		switch (this.purchaseTypeId) {
			case 15:
				if (!this.creditsUsed)
					return 0;
				totalPrice = this.price;
				discount = this.creditsUsed;
				break;
			default:
				totalPrice = this.price;
				discount = this.savings;
				break;
		}
		return Math.floor(100 - (((totalPrice - discount) / totalPrice) * 100));
    }
    
	public get priceWithTaxesAndFees(): number {
		return this.price + this.taxesAndFees;
	}

	public get priceLessDiscount(): number {
		return this.price - this.savings;
	}

    public get dailyPrice(): number
    {
        return this.price / this.nights;
    }

    public get key(): string
    {
        return this.unitType + "|" + this.unitTypeName;
    }

    public get totalWithCredits(): number
    {
        return this.price - this.creditsUsed;
    }

    public get grandTotal(): number
	{
		let discount: number;
		switch (this.purchaseTypeId) {
			case 15:
				if (!this.creditsUsed)
					return 0;
				discount = this.creditsUsed;
				break;
			default:
				discount = this.savings;
				break;
		}

		return this.priceWithTaxesAndFees + this.insurance - discount;
    }

    public get isUpgradeAvailable(): boolean{
        return this['resortInventoryPriceList'].where(p => p.purchaseTypeCategoryId === 3).count() > 0;
    }

    public get rewardWithUpgrade(): number {
        return this['resortInventoryPriceList'].firstOrDefault(p => p.purchaseTypeCategoryId === 3).reward;
    }

    public get upgradeCobrandId(): number {
        return this.isUpgradeAvailable ? this['resortInventoryPriceList'].firstOrDefault(p => p.purchaseTypeCategoryId === 3).upgradeCobrandId : 0;
    }

    public get upgradePurchaseTypeId(): number {
        return this.isUpgradeAvailable ? this['resortInventoryPriceList'].firstOrDefault(p => p.purchaseTypeCategoryId === 3).upgradePurchaseTypeId: 0;
    }
}

export class ResortGuestExtension extends Entity
{
    public index: number;
    public ordinal: string;
    public DobMonth: number;
    public DobDay: number;
    public DobYear: number;
    public hasValidDateOfBirth():boolean{
        return this.DobDay !== undefined && this.DobDay!== undefined && this.DobYear!== undefined;
    }

    public get age():number{
        if(this.hasValidDateOfBirth){
            var dateOfBirth = new Date(this.DobYear, this.DobMonth, this.DobDay);
            var date = new Date().toISOString()
            return moment().diff(dateOfBirth.toISOString(), 'year', true);
        }
        return 0;
    }
}

export class ResortAllInclusiveRuleExtension extends Entity {
    public hasMatch(checkInDate: Date, unitTypeId: number, guestNumber: number, age: number): boolean
{
    if ((this["checkInDateStart"] === null && this["checkInDateEnd"] === null) || (checkInDate > this["checkInDateStart"] && checkInDate< this["checkInDateEnd"])) {
        if (this["unitTypeId"] === -99 || unitTypeId === this["unitTypeId"]) {
            if ((this["guestNumberStart"] === 0 && this["guestNumberEnd"] === 0) || (guestNumber >= this["guestNumberStart"] && guestNumber <= this["guestNumberEnd"])) {
                if ((this["ageStart"] === 0 && this["ageEnd"] === 0) || (age >= this["ageStart"] && age < this["ageEnd"]+1)) {
                    return true;
                }
            }
        }
    }

    return false;
}

public equals (cobj:ResortAllInclusiveRuleExtension): boolean{
    if( (this["checkInDateStart"] === cobj["checkInDateStart"]) && (this["checkInDateEnd"] === cobj["checkInDateEnd"]) &&
    (this["guestNumberStart"] === cobj["guestNumberStart"]) && (this["guestNumberEnd"] === cobj["guestNumberEnd"]) &&
    (this["unitType"] === cobj["unitType"]) && (this["ageStart"] === cobj["ageStart"]) && (this["ageEnd"] === cobj["ageEnd"]))
        return true;
    else 
        return false;
}

}

export class ResortInventoryGroup
{
    private _featuredInventory: ResortInventory;

    public inventories: ResortInventory[];
    public resort: ResortDetail;

    public get featuredInventory(): ResortInventory
    {
        if (!this._featuredInventory)
        {
            let minRate: number = this.inventories.min(i => i.discountRate);
            this._featuredInventory = this.inventories.firstOrDefault(i => i.discountRate === minRate);
        }
        return this._featuredInventory;
    }

    constructor(resort: ResortDetail, inventories: ResortInventory[])
    {
        this.inventories = inventories; 
        this.resort = resort;
    }
}




// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/models/browser.model.ts